import { toPng } from 'html-to-image'
import { getViewportForBounds, type Rect } from '@xyflow/react'
import type { AppNode, NodeKind } from '../types'
import {
  getAbsolutePosition,
  NODE_DEFAULTS,
  useFlowStore,
} from '../store/flowStore'
import { downloadUrl } from './download'

function absoluteBounds(nodes: AppNode[]): Rect | null {
  if (nodes.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const node of nodes) {
    const abs = getAbsolutePosition(node, nodes)
    const defaults = NODE_DEFAULTS[(node.type ?? 'process') as NodeKind]
    const width = node.measured?.width ?? node.width ?? defaults.width
    const height = node.measured?.height ?? node.height ?? defaults.height
    minX = Math.min(minX, abs.x)
    minY = Math.min(minY, abs.y)
    maxX = Math.max(maxX, abs.x + width)
    maxY = Math.max(maxY, abs.y + height)
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

function shouldIncludeInExport(domNode: HTMLElement): boolean {
  if (!(domNode instanceof Element)) return true
  if (domNode.classList.contains('react-flow__minimap')) return false
  if (domNode.classList.contains('react-flow__controls')) return false
  if (domNode.classList.contains('react-flow__panel')) return false
  if (domNode.classList.contains('node-toolbar-actions')) return false
  if (domNode.classList.contains('react-flow__resize-control')) return false
  if (domNode.classList.contains('app-handle')) return false
  if (domNode.classList.contains('react-flow__handle')) return false
  return true
}

function waitNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

export async function exportToPng(
  nodes: AppNode[],
  filename: string,
): Promise<void> {
  const bounds = absoluteBounds(nodes)
  const viewportEl = document.querySelector<HTMLElement>(
    '.react-flow__viewport',
  )
  if (!bounds || !viewportEl) {
    throw new Error('Não há nada para exportar.')
  }

  const padding = 40
  const width = Math.min(Math.ceil(bounds.width) + padding * 2, 4096)
  const height = Math.min(Math.ceil(bounds.height) + padding * 2, 4096)
  const viewport = getViewportForBounds(bounds, width, height, 0.2, 2, 0.06)

  // Sempre exporta como modo claro (fundo claro + linhas escuras).
  const { colorMode, setColorMode } = useFlowStore.getState()
  const editor = document.querySelector('.fluxograma-editor')
  setColorMode('light')
  editor?.classList.add('fluxograma-exporting-light')

  try {
    await waitNextPaint()

    // Cores devem ser hex/atributos inline nos nós (não só CSS var) —
    // html-to-image não resolve var(--node-color) dentro de SVG e vira preto.
    const dataUrl = await toPng(viewportEl, {
      backgroundColor: '#f8fafc',
      width,
      height,
      pixelRatio: 2,
      cacheBust: true,
      filter: shouldIncludeInExport,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
    })

    downloadUrl(dataUrl, filename)
  } finally {
    setColorMode(colorMode)
    editor?.classList.remove('fluxograma-exporting-light')
  }
}
